import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { api } from '../api/client.js'
import type { Staff, Team } from '../api/types.js'
import { initials } from '../lib/format.js'
import { Icon } from './icons.js'

export function useStaff() {
  return useQuery({ queryKey: ['staff'], queryFn: () => api<Staff[]>('/api/staff'), staleTime: 60_000 })
}

export function useTeams() {
  return useQuery({ queryKey: ['teams'], queryFn: () => api<Team[]>('/api/teams'), staleTime: 60_000 })
}

export function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'lg' }) {
  return <span className={`avatar ${size}`}>{initials(name)}</span>
}

function TeamAvatar() {
  return (
    <span className="avatar sm team" aria-hidden="true">
      <Icon name="users" size={13} />
    </span>
  )
}

interface PickItem {
  key: string
  label: string
  sub?: string
  inactive?: boolean
  avatar: ReactNode
  search: string
}

/**
 * Picking one of a few hundred: type part of the name or phone, arrow down,
 * enter. Loaded once and filtered on the client; five hundred rows are
 * nothing compared with a dropdown that long.
 */
function SearchPicker({
  items,
  loading,
  value,
  onPick,
  emptyLabel,
  placeholder,
  autoFocus,
  dense,
}: {
  items: PickItem[]
  loading: boolean
  value: string
  onPick: (key: string) => void
  emptyLabel: string
  placeholder?: string
  autoFocus?: boolean
  dense?: boolean
}) {
  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const wrapRef = useRef<HTMLDivElement>(null)

  const list = useMemo(() => {
    const q = term.trim().toLowerCase()
    const compact = q.replace(/\s/g, '')
    const found = q ? items.filter((i) => i.search.includes(q) || (compact.length > 2 && i.search.replace(/\s/g, '').includes(compact))) : items
    return found.slice(0, 60)
  }, [items, term])

  const selected = items.find((i) => i.key === value) ?? null

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  useEffect(() => setActive(0), [term])

  function choose(key: string) {
    onPick(key)
    setTerm('')
    setOpen(false)
  }

  if (value && selected && !open) {
    return (
      <div className={`picker picked ${dense ? 'dense' : ''}`}>
        {selected.avatar}
        <span className="picked-text">
          <span className="strong">{selected.label}</span>
          {selected.inactive && <span className="muted small"> (deaktiviert)</span>}
        </span>
        <button
          type="button"
          className="btn ghost small"
          onClick={() => {
            onPick('')
            setOpen(true)
          }}
        >
          Ändern
        </button>
      </div>
    )
  }

  return (
    <div className={`picker ${dense ? 'dense' : ''}`} ref={wrapRef}>
      <input
        type="search"
        value={term}
        autoFocus={autoFocus}
        placeholder={placeholder ?? `${emptyLabel}, zum Zuweisen Namen tippen`}
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setTerm(event.target.value)
          setOpen(true)
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActive((i) => Math.min(i + 1, list.length - 1))
          } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (event.key === 'Enter' && open) {
            event.preventDefault()
            if (list[active]) choose(list[active]!.key)
          } else if (event.key === 'Escape') {
            setOpen(false)
          }
        }}
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && (
        <ul className="picker-list" role="listbox">
          {!term && (
            <li role="option" aria-selected={false} className="muted" onMouseDown={(e) => { e.preventDefault(); choose('') }}>
              <span className="text">{emptyLabel}</span>
            </li>
          )}
          {loading && <li className="muted small">Lädt …</li>}
          {!loading && list.length === 0 && <li className="muted small">Nichts gefunden.</li>}
          {list.map((item, index) => (
            <li
              key={item.key}
              role="option"
              aria-selected={index === active}
              className={index === active ? 'active' : undefined}
              onMouseEnter={() => setActive(index)}
              onMouseDown={(event) => {
                event.preventDefault()
                choose(item.key)
              }}
            >
              {item.avatar}
              <span className="text">
                <span className="strong">{item.label}</span>
                {item.sub && <span className="muted small">{item.sub}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function personItem(person: Staff): PickItem {
  return {
    key: person.id,
    label: person.name,
    inactive: !person.active,
    avatar: <Avatar name={person.name} />,
    sub: [person.phone, person.planningAccess ? 'Tourenplanung' : null, person.active ? null : 'deaktiviert', person.activeDevices === 0 ? 'nicht angemeldet' : null]
      .filter(Boolean)
      .join(', '),
    search: `${person.name} ${person.phone ?? ''} ${person.email ?? ''}`.toLowerCase(),
  }
}

interface PickerProps {
  value: string
  onChange: (id: string, person: Staff | null) => void
  /** Text for the empty choice, e.g. "Nicht zugewiesen" or "Alle". */
  emptyLabel?: string
  /** Also offer deactivated people (for historic records). */
  includeInactive?: boolean
  placeholder?: string
  autoFocus?: boolean
  /** Compact height for toolbars. */
  dense?: boolean
}

export function PersonPicker({ value, onChange, emptyLabel = 'Nicht zugewiesen', includeInactive, placeholder, autoFocus, dense }: PickerProps) {
  const staff = useStaff()
  const items = useMemo(
    () => (staff.data ?? []).filter((p) => includeInactive || p.active || p.id === value).map(personItem),
    [staff.data, includeInactive, value],
  )
  return (
    <SearchPicker
      items={items}
      loading={staff.isLoading}
      value={value}
      onPick={(key) => onChange(key, staff.data?.find((p) => p.id === key) ?? null)}
      emptyLabel={emptyLabel}
      placeholder={placeholder}
      autoFocus={autoFocus}
      dense={dense}
    />
  )
}

export interface Assignment {
  assigneeId: string | null
  assigneeTeamId: string | null
}

/**
 * A person or a group. Groups show up in the same search as people, marked
 * with the group icon: typing "Stud" finds "Studierende Nord" next to anyone
 * called Stuhr.
 */
export function AssigneePicker({
  value,
  onChange,
  emptyLabel = 'Nicht zugewiesen',
  placeholder,
  dense,
}: {
  value: Assignment
  onChange: (value: Assignment) => void
  emptyLabel?: string
  placeholder?: string
  dense?: boolean
}) {
  const staff = useStaff()
  const teams = useTeams()
  const items = useMemo(() => {
    const teamItems: PickItem[] = (teams.data ?? []).map((team) => ({
      key: `team:${team.id}`,
      label: team.name,
      avatar: <TeamAvatar />,
      sub: `Gruppe, ${team.members.length} ${team.members.length === 1 ? 'Person' : 'Personen'}: ${team.members
        .slice(0, 4)
        .map((m) => m.name.split(' ')[0])
        .join(', ')}${team.members.length > 4 ? ' …' : ''}`,
      search: `${team.name} gruppe ${team.members.map((m) => m.name).join(' ')}`.toLowerCase(),
    }))
    const people = (staff.data ?? []).filter((p) => p.active || p.id === value.assigneeId).map(personItem)
    return [...teamItems, ...people]
  }, [teams.data, staff.data, value.assigneeId])

  const key = value.assigneeTeamId ? `team:${value.assigneeTeamId}` : (value.assigneeId ?? '')
  return (
    <SearchPicker
      items={items}
      loading={staff.isLoading || teams.isLoading}
      value={key}
      onPick={(picked) => {
        if (!picked) onChange({ assigneeId: null, assigneeTeamId: null })
        else if (picked.startsWith('team:')) onChange({ assigneeId: null, assigneeTeamId: picked.slice(5) })
        else onChange({ assigneeId: picked, assigneeTeamId: null })
      }}
      emptyLabel={emptyLabel}
      placeholder={placeholder ?? `${emptyLabel}, Person oder Gruppe tippen`}
      dense={dense}
    />
  )
}

/** "Lena Kaya" or "Gruppe Studierende Nord", for tables. */
export function assigneeLabel(task: { assigneeName?: string | null; teamName?: string | null }): string | null {
  if (task.assigneeName) return task.assigneeName
  if (task.teamName) return `Gruppe ${task.teamName}`
  return null
}
